/**
 * One row of the Detail table: a client, a date, a rate, an activity.
 *
 * Everything on it is editable, and each field commits on blur rather than
 * behind a Save button — a table where every row has a Save is a table nobody
 * edits. Nothing is stored optimistically: the server owns the arithmetic, so
 * the row re-renders from what comes back.
 *
 * Expanding it shows the individual start/stop stretches that add up to the
 * day's total. That is where the rounding becomes visible: a stop typed as
 * 10:07 reads back as 10:15, because the stored end *is* the rounded end.
 *
 * An invoiced row is inert. Every control is replaced by the invoice it went
 * on and a button to release it, because changing hours behind a document that
 * has already been sent is not an edit, it is a discrepancy.
 */

import { useEffect, useState } from 'react'

import type { ConsultingClient, RateKind, TimeSegment, WorkDay } from '@/api/types'
import { AutoGrowTextarea } from '@/components/AutoGrowTextarea'
import { Field } from '@/components/ui'
import {
  formatClock,
  formatHours,
  formatMoney,
  formatWorkDateLong,
  rateLabel,
  toLocalInput,
  fromLocalInput,
} from '@/lib/consulting'

interface DayRowProps {
  day: WorkDay
  clients: ConsultingClient[]
  selected: boolean
  onSelect: (id: number, selected: boolean) => void
  onPatch: (id: number, patch: Record<string, unknown>) => Promise<void>
  onDelete: (id: number) => Promise<void>
  onUnarchive: (id: number) => Promise<void>
  onPatchSegment: (id: number, patch: Record<string, unknown>) => Promise<void>
  onDeleteSegment: (id: number) => Promise<void>
}

export function ConsultingDayRow({
  day,
  clients,
  selected,
  onSelect,
  onPatch,
  onDelete,
  onUnarchive,
  onPatchSegment,
  onDeleteSegment,
}: DayRowProps) {
  const [open, setOpen] = useState(false)
  const [activity, setActivity] = useState(day.activity)
  const invoiced = day.invoice_id !== null

  // The composed activity changes whenever a segment's note does, so the local
  // draft has to follow the server's value rather than shadow it.
  useEffect(() => {
    setActivity(day.activity)
  }, [day.activity])

  function commitActivity() {
    if (activity === day.activity) return
    void onPatch(day.id, { activity: activity.trim() })
  }

  return (
    <li className="border-b border-line last:border-b-0">
      <div className={`flex flex-col gap-2 px-3 py-2.5 ${invoiced ? 'bg-surface-2' : ''}`}>
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            className="mt-1 size-4 shrink-0"
            checked={selected}
            disabled={invoiced}
            onChange={(event) => onSelect(day.id, event.target.checked)}
            aria-label={`Select ${day.client_name} on ${day.work_date}`}
          />

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <span className="font-semibold text-navy">{day.client_name}</span>
              <span className="text-xs text-ink-3">{formatWorkDateLong(day.work_date)}</span>
              <span className="pill bg-navy-soft text-navy">{formatHours(day.hours)} h</span>
              {day.rate_kind === 'joint_venture' ? (
                <span className="pill bg-info-light text-info-text">JV</span>
              ) : (
                <span className="text-xs text-ink-3">
                  {rateLabel(day.rate_kind, day.rate)}
                  {day.amount !== null && (
                    <span className="ml-1 font-semibold text-ink-2">
                      {formatMoney(day.amount)}
                    </span>
                  )}
                </span>
              )}
              {invoiced && (
                <span className="pill bg-success-light text-success-text">
                  Invoice #{day.invoice_id}
                </span>
              )}
            </div>

            {invoiced ? (
              <p className="mt-1 text-sm text-ink-2">{day.activity || <em>No activity noted</em>}</p>
            ) : (
              <AutoGrowTextarea
                className="field mt-1 text-sm"
                value={activity}
                placeholder="Activity — blank goes back to joining the notes below"
                onChange={(event) => setActivity(event.target.value)}
                onBlur={commitActivity}
                aria-label="Activity"
              />
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 pl-6">
          <button
            type="button"
            className="self-center text-xs font-semibold text-navy hover:underline"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
          >
            {open ? 'Hide' : `${day.segments.length} stretch${day.segments.length === 1 ? '' : 'es'}`}
          </button>

          {day.activity_is_override && !invoiced && (
            <button
              type="button"
              className="self-center text-xs text-ink-3 hover:underline"
              onClick={() => void onPatch(day.id, { activity: null })}
              title="Go back to joining the notes on each stretch"
            >
              Use the notes instead
            </button>
          )}

          {invoiced ? (
            <button
              type="button"
              className="btn btn-neutral btn-sm ml-auto"
              onClick={() => void onUnarchive(day.id)}
            >
              Un-archive
            </button>
          ) : (
            <>
              <RateControl day={day} onPatch={onPatch} />
              <ClientControl day={day} clients={clients} onPatch={onPatch} />
              <Field label="Date" className="w-36">
                <input
                  type="date"
                  className="field py-1 text-xs"
                  value={day.work_date}
                  onChange={(event) => void onPatch(day.id, { work_date: event.target.value })}
                />
              </Field>
              <button
                type="button"
                className="btn btn-neutral btn-sm ml-auto"
                onClick={() => void onDelete(day.id)}
              >
                Delete
              </button>
            </>
          )}
        </div>

        {open && (
          <ul className="m-0 list-none rounded-md bg-surface-2 p-2 pl-6">
            {day.segments.map((segment) => (
              <SegmentRow
                key={segment.id}
                segment={segment}
                locked={invoiced}
                onPatch={onPatchSegment}
                onDelete={onDeleteSegment}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  )
}

function RateControl({
  day,
  onPatch,
}: {
  day: WorkDay
  onPatch: DayRowProps['onPatch']
}) {
  const [draft, setDraft] = useState(day.rate === null ? '' : String(day.rate))

  useEffect(() => {
    setDraft(day.rate === null ? '' : String(day.rate))
  }, [day.rate])

  return (
    // Labelled, like every other control in the feature. Unlabelled, a dropdown
    // reading "An hourly rate" next to a number box looks like part of the
    // number rather than the switch that removes it.
    <>
      <Field label="Bills at" className="w-36">
        <select
          className="field py-1 text-xs"
          value={day.rate_kind}
          onChange={(event) => void onPatch(day.id, { rate_kind: event.target.value as RateKind })}
        >
          <option value="hourly">An hourly rate</option>
          <option value="joint_venture">Joint venture</option>
        </select>
      </Field>
      {day.rate_kind === 'hourly' && (
        <Field label="Rate" className="w-24">
          <input
            type="number"
            min="0"
            step="0.01"
            className="field py-1 text-xs"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              const next = draft === '' ? null : Number(draft)
              if (next !== day.rate) void onPatch(day.id, { rate: next })
            }}
          />
        </Field>
      )}
    </>
  )
}

function ClientControl({
  day,
  clients,
  onPatch,
}: {
  day: WorkDay
  clients: ConsultingClient[]
  onPatch: DayRowProps['onPatch']
}) {
  return (
    <Field label="Client" className="w-44">
      <select
        className="field py-1 text-xs"
        value={day.client_id}
        onChange={(event) => void onPatch(day.id, { client_id: Number(event.target.value) })}
      >
        {/* An archived client is not in the list, and without this the select
            would fall back to whatever sorts first — silently reassigning the
            row to a different client just by rendering it. */}
        {!clients.some((client) => client.id === day.client_id) && (
          <option value={day.client_id}>{day.client_name}</option>
        )}
        {clients.map((client) => (
          <option key={client.id} value={client.id}>
            {client.name}
          </option>
        ))}
      </select>
    </Field>
  )
}

function SegmentRow({
  segment,
  locked,
  onPatch,
  onDelete,
}: {
  segment: TimeSegment
  locked: boolean
  onPatch: DayRowProps['onPatchSegment']
  onDelete: DayRowProps['onDeleteSegment']
}) {
  const [note, setNote] = useState(segment.note ?? '')

  useEffect(() => {
    setNote(segment.note ?? '')
  }, [segment.note])

  if (segment.ended_at === null) {
    return (
      <li className="flex flex-wrap items-center gap-2 py-1 text-xs text-ink-2">
        <span className="pill bg-crimson-soft text-crimson">Running</span>
        <span>started {formatClock(segment.started_at)}</span>
        <span className="text-ink-3">{segment.note}</span>
      </li>
    )
  }

  if (locked) {
    return (
      <li className="flex flex-wrap items-center gap-2 py-1 text-xs text-ink-2">
        <span className="tabular-nums">
          {formatClock(segment.started_at)} – {formatClock(segment.ended_at)}
        </span>
        <span className="tabular-nums">{formatHours(segment.hours)} h</span>
        <span className="text-ink-3">{segment.note}</span>
      </li>
    )
  }

  return (
    <li className="flex flex-wrap items-end gap-2 py-1">
      {/* Two identical datetime boxes side by side are unreadable unlabelled:
          nothing but position says which one is the stop. */}
      <Field label="Started" className="w-52">
        <input
          type="datetime-local"
          className="field py-1 text-xs"
          value={toLocalInput(segment.started_at)}
          onChange={(event) =>
            void onPatch(segment.id, { started_at: fromLocalInput(event.target.value) })
          }
        />
      </Field>
      <Field label="Stopped" className="w-52">
        <input
          type="datetime-local"
          className="field py-1 text-xs"
          value={toLocalInput(segment.ended_at)}
          onChange={(event) =>
            void onPatch(segment.id, { ended_at: fromLocalInput(event.target.value) })
          }
        />
      </Field>
      <span className="pill mb-1.5 bg-surface-3 text-ink-2 tabular-nums">
        {formatHours(segment.hours)} h
      </span>
      <Field label="Note" className="min-w-40 flex-1">
        <input
          className="field py-1 text-xs"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          onBlur={() => {
            if (note !== (segment.note ?? '')) void onPatch(segment.id, { note })
          }}
        />
      </Field>
      <button
        type="button"
        className="btn btn-neutral btn-sm"
        onClick={() => void onDelete(segment.id)}
      >
        Delete
      </button>
    </li>
  )
}
